import React, { useState } from "react";
import { TrendingUp, MapPin, Star, BarChart2, Globe, Megaphone, FileText, ArrowRight, CheckCircle, Share2 } from "lucide-react";
import { useShop } from "../context/ShopContext";
import { PageTitle } from "../components/Shared";
import ContactModal from "../components/ContactModal";
import Seo from "../components/Seo";

const content = {
  it: {
    seoTitle: "LOCAL SEO PRO – Più clienti aziendali ogni mese | IdeaMedia",
    seoDesc: "Scopri il metodo Local SEO PRO di IdeaMedia per attrarre clienti aziendali ogni mese in modo organico: Google Maps, portali di settore, posizionamento e reportistica mensile. A partire da 159€/mese.",
    hero: {
      badge: "Servizio professionale",
      title: "Come avere più clienti aziendali ogni mese?",
      subtitle: "Come attrarre naturalmente più clienti per la propria attività e aumentare le vendite?",
    },
    intro: {
      label: "Metodo naturale (non a pagamento) per attrarre clienti — Lead Magnet",
      text: "Dopo anni di ricerca e sviluppo, abbiamo perfezionato un metodo efficace ed economico per acquisire nuovi clienti senza dover investire grandi cifre in pubblicità. Otteniamo risultati organici attraverso i motori di ricerca, Google Maps e i principali portali di settore: clienti che cercano attivamente il tuo servizio arrivano da soli.",
    },
    howTitle: "Come otteniamo maggiore visibilità e contatti ogni mese per la tua azienda?",
    howText: "Lo facciamo tramite il nostro pacchetto di servizi chiamato LOCAL SEO PRO, che lavora su più fronti contemporaneamente: portiamo il tuo sito e la tua scheda Google al 1° posto per le ricerche della tua zona — il posizionamento è incluso nel prezzo, senza spendere un euro in pubblicità — e pubblichiamo in automatico i tuoi contenuti sul sito e sui social. Ogni contatto è tracciato e misurato nel report mensile.",
    features: [
      { icon: Globe, text: "Sviluppo o Restyling del sito web" },
      { icon: TrendingUp, text: "Posizionamento del sito al 1° posto sui motori di ricerca, incluso nel prezzo" },
      { icon: MapPin, text: "Inserimento e ottimizzazione Google Maps" },
      { icon: MapPin, text: "Posizionamento mappe al 1° posto" },
      { icon: Star, text: "Gestione profilo e recensioni online" },
      { icon: Megaphone, text: "Pubblicazione sui principali portali di settore" },
      { icon: Share2, text: "Pubblicazione automatica dei contenuti sul sito e sui social" },
      { icon: BarChart2, text: "Reportistica e monitoraggio mensile" },
      { icon: FileText, text: "Possibilità di promozione sui portali" },
    ],
    card1: "Sito web ottimizzato e posizionato al 1° posto",
    card2: "Google Maps al 1° posto",
    card3: "Presente sui principali portali di settore",
    price: "A partire da",
    priceVal: "159€",
    pricePer: "al mese",
    ctaBtn: "Chiedi maggiori informazioni",
    results: {
      title: "Risultati concreti, misurabili ogni mese",
      items: [
        { num: "1°", label: "Posto su Google e Google Maps, incluso nel prezzo" },
        { num: "0€", label: "Speso in pubblicità: contatti 100% organici" },
        { num: "Auto", label: "Contenuti pubblicati su sito e social, in automatico" },
      ],
    },
    whyTitle: "Perché scegliere il metodo organico?",
    whyItems: [
      "Costo mensile fisso e prevedibile, senza sorprese",
      "Clienti che ti cercano attivamente — non pubblicità invasiva",
      "Risultati che crescono nel tempo e non si azzerano",
      "Reportistica trasparente: sai sempre cosa sta succedendo",
      "Nessun vincolo: il tuo profilo e i tuoi contenuti restano tuoi",
    ],
  },
  en: {
    seoTitle: "LOCAL SEO PRO – More business clients every month | IdeaMedia",
    seoDesc: "Discover IdeaMedia's Local SEO PRO method to attract business clients every month organically: Google Maps, industry portals, search ranking and monthly reporting. From €159/month.",
    hero: {
      badge: "Professional service",
      title: "How to get more business clients every month?",
      subtitle: "How to naturally attract more clients to your business and increase sales?",
    },
    intro: {
      label: "Natural (non-paid) method to attract clients — Lead Magnet",
      text: "After years of research and development, we have perfected an effective and affordable method to acquire new clients without large advertising investments. We achieve organic results through search engines, Google Maps and the leading industry portals: clients who are actively searching for your service come to you.",
    },
    howTitle: "How do we generate more visibility and contacts every month for your business?",
    howText: "We do it through our service package called LOCAL SEO PRO, which works on several fronts at the same time: we take your website and your Google listing to 1st place for searches in your area — ranking is included in the price, with no money spent on advertising — and we publish your content automatically on your site and social channels. Every contact is tracked and measured in the monthly report.",
    features: [
      { icon: Globe, text: "Website development or restyling" },
      { icon: TrendingUp, text: "1st place search engine ranking for your website, included in the price" },
      { icon: MapPin, text: "Google Maps insertion and optimisation" },
      { icon: MapPin, text: "1st place Maps ranking" },
      { icon: Star, text: "Profile and online reviews management" },
      { icon: Megaphone, text: "Publication on the leading industry portals" },
      { icon: Share2, text: "Automatic content publishing on your website and social channels" },
      { icon: BarChart2, text: "Monthly reporting and monitoring" },
      { icon: FileText, text: "Option for portal promotions" },
    ],
    card1: "Optimised website, ranked in 1st place",
    card2: "Google Maps 1st place ranking",
    card3: "Listed on the leading industry portals",
    price: "Starting from",
    priceVal: "€159",
    pricePer: "per month",
    ctaBtn: "Request more information",
    results: {
      title: "Concrete results, measurable every month",
      items: [
        { num: "1st", label: "Place on Google and Google Maps, included in the price" },
        { num: "€0", label: "Spent on advertising: 100% organic contacts" },
        { num: "Auto", label: "Content published on your site and socials, automatically" },
      ],
    },
    whyTitle: "Why choose the organic method?",
    whyItems: [
      "Fixed, predictable monthly cost — no surprises",
      "Clients who actively search for you — not intrusive advertising",
      "Results that grow over time and don't reset",
      "Transparent reporting: you always know what's happening",
      "No lock-in: your profile and content remain yours",
    ],
  },
};

const LocalSeoPro = () => {
  const { lang } = useShop();
  const c = content[lang];
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <div data-testid="local-seo-pro-page">
      <Seo title={c.seoTitle} description={c.seoDesc} />
      <PageTitle title="LOCAL SEO PRO" />

      {/* Hero text */}
      <section className="idm-section" style={{ paddingBottom: 0 }}>
        <div className="idm-container idm-center">
          <span style={{
            display: "inline-block", background: "var(--idm-yellow, #f5c800)", color: "#111",
            fontWeight: 700, fontSize: "0.75rem", letterSpacing: "0.08em", textTransform: "uppercase",
            padding: "4px 14px", borderRadius: 20, marginBottom: 18,
          }}>
            {c.hero.badge}
          </span>
          <h1 className="idm-section-title" style={{ fontSize: "clamp(1.6rem, 4vw, 2.6rem)", maxWidth: 720, margin: "0 auto 16px" }}>
            {c.hero.title}
          </h1>
          <span className="idm-separator" />
          <p className="idm-text idm-narrow idm-center-text" style={{ fontSize: "1.1rem" }}>
            {c.hero.subtitle}
          </p>
        </div>
      </section>

      {/* Hero image — laptop con Google */}
      <div style={{ width: "100%", maxHeight: 420, overflow: "hidden", lineHeight: 0 }}>
        <img
          src="/images/seo-clients.webp"
          alt="Posizionamento Google – LOCAL SEO PRO IdeaMedia"
          style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 30%" }}
          loading="eager"
        />
      </div>

      {/* Intro */}
      <section className="idm-section idm-section-gray">
        <div className="idm-container" style={{ maxWidth: 780 }}>
          <p style={{
            fontWeight: 700, fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.07em",
            color: "var(--idm-yellow-dark, #b89000)", marginBottom: 12,
          }}>
            {c.intro.label}
          </p>
          <p className="idm-text">{c.intro.text}</p>
        </div>
      </section>

      {/* How it works + Features */}
      <section className="idm-section">
        <div className="idm-container">
          <h2 className="idm-section-title" style={{ maxWidth: 680, margin: "0 auto 12px" }}>
            {c.howTitle}
          </h2>
          <span className="idm-separator" />
          <p className="idm-text idm-narrow idm-center-text" style={{ marginBottom: 40 }}>
            {c.howText}
          </p>

          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
            gap: 20, maxWidth: 900, margin: "0 auto",
          }}>
            {c.features.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} style={{
                  display: "flex", alignItems: "flex-start", gap: 14,
                  background: "var(--idm-bg-card, #f8f8f8)", borderRadius: 12,
                  padding: "18px 20px", border: "1px solid var(--idm-border, #eee)",
                }}>
                  <Icon size={22} strokeWidth={1.6} style={{ color: "var(--idm-yellow-dark, #b89000)", flexShrink: 0, marginTop: 2 }} />
                  <span style={{ fontWeight: 500, fontSize: "0.97rem" }}>{f.text}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Tre punti principali Local SEO — immagini */}
      <section className="idm-section idm-section-gray" style={{ paddingTop: 48 }}>
        <div className="idm-container">
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 28, maxWidth: 980, margin: "0 auto",
          }}>
            {[
              {
                src: "/images/sito-pro.jpg",
                alt: "Sito web professionale ottimizzato su tutti i dispositivi",
                label: c.card1,
              },
              {
                src: "/images/seo-googlemaps.jpg",
                alt: "Google Maps ottimizzato – posizionamento locale",
                label: c.card2,
              },
              {
                src: "/images/seo-portali.jpg",
                alt: "Pubblicazione nei portali di settore online",
                label: c.card3,
              },
            ].map((card, i) => (
              <div key={i} style={{
                borderRadius: 16, overflow: "hidden",
                boxShadow: "0 6px 28px rgba(0,0,0,0.10)",
                background: "#fff",
                display: "flex", flexDirection: "column",
              }}>
                <img
                  src={card.src}
                  alt={card.alt}
                  style={{ width: "100%", height: 220, objectFit: "cover", display: "block" }}
                  loading="lazy"
                />
                <div style={{
                  padding: "16px 20px",
                  display: "flex", alignItems: "center", gap: 10,
                  borderTop: "3px solid var(--idm-yellow, #f5c800)",
                }}>
                  <span style={{ fontWeight: 700, fontSize: "0.95rem" }}>{card.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats panel scuro */}
      <section style={{ background: "var(--idm-dark, #1a1a2e)", color: "#fff", padding: "60px 20px" }}>
        <div className="idm-container idm-center">
          <h2 className="idm-section-title" style={{ color: "#fff", marginBottom: 12 }}>{c.results.title}</h2>
          <span className="idm-separator" />
          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 40, marginTop: 32 }}>
            {c.results.items.map((r, i) => (
              <div key={i} style={{ textAlign: "center" }}>
                <div style={{ fontSize: "clamp(2.2rem, 5vw, 3.2rem)", fontWeight: 800, color: "var(--idm-yellow, #f5c800)", lineHeight: 1 }}>
                  {r.num}
                </div>
                <div style={{ marginTop: 8, fontSize: "0.9rem", opacity: 0.85, maxWidth: 160 }}>{r.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why organic — testo + immagine affiancata */}
      <section className="idm-section">
        <div className="idm-container idm-lseo-why" style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 48, alignItems: "center", maxWidth: 960,
        }}>
          <div>
            <h2 className="idm-section-title" style={{ textAlign: "left", marginBottom: 24 }}>{c.whyTitle}</h2>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 14 }}>
              {c.whyItems.map((item, i) => (
                <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                  <CheckCircle size={20} strokeWidth={2} style={{ color: "var(--idm-yellow-dark, #b89000)", flexShrink: 0, marginTop: 2 }} />
                  <span style={{ fontSize: "1rem" }}>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <img
              src="/images/seo-business.jpg"
              alt="Presenza online e clienti dal web"
              style={{
                width: "100%", borderRadius: 16,
                boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
              }}
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* Price + CTA */}
      <section className="idm-section idm-section-gray">
        <div className="idm-container idm-center">
          <div style={{
            display: "inline-flex", flexDirection: "column", alignItems: "center",
            background: "var(--idm-yellow, #f5c800)", borderRadius: 20,
            padding: "32px 48px", marginBottom: 32,
          }}>
            <span style={{ fontSize: "0.85rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              {c.price}
            </span>
            <span style={{ fontSize: "clamp(2.8rem, 7vw, 4rem)", fontWeight: 900, lineHeight: 1.1, color: "#111" }}>
              {c.priceVal}
            </span>
            <span style={{ fontSize: "0.9rem", fontWeight: 600 }}>{c.pricePer}</span>
          </div>
          <div>
            <button
              className="idm-btn-yellow"
              style={{ animation: "none", fontSize: "1.05rem", padding: "14px 36px", display: "inline-flex", alignItems: "center", gap: 10 }}
              onClick={() => setModalOpen(true)}
              data-testid="local-seo-cta"
            >
              {c.ctaBtn} <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </section>

      <ContactModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={c.ctaBtn}
        defaultSubject="Richiesta informazioni LOCAL SEO PRO"
      />
    </div>
  );
};

export default LocalSeoPro;
